import { Offcanvas, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import { EditEntityDrawerProps } from '../IAP/EntityListProps';
import DrawerWrapper, { DrawerFooter } from '../IAP/DrawerWrapper';
import FormTextField from '../IAP/formFields/FormTextField';
import {
  GetIncidentActionResponse,
  useUpdateIncidentAction,
  IncidentActionBase,
} from './hooks/incidentActionHooks';
import { Action, useEditAction } from './hooks/actionHooks';
import FormSelectField from '../IAP/formFields/FormSelectField';
import FormCheckboxField from '../IAP/formFields/FormCheckboxField';
import FormDateTimeField from '../IAP/formFields/FormDateTimeField';
import { ACTION_STATUS_OPTIONS, INCIDENT } from 'components/IAP/ICSConstants';
import { isIncidentAction } from '../IAP/utils/typeguard';
import DeleteModal from '../DesignSystems/DeleteModal';
import { useIncidentRoster } from '../IAP/hooks/incidentRoster/useIncidentRoster';

type FormFields = Omit<
  IncidentActionBase,
  'id' | 'action_id' | 'utc_offset' | 'parent_id'
>;

const OpenActionEditDrawer: React.FC<
  EditEntityDrawerProps<GetIncidentActionResponse | Action>
> = ({ toggle, selectedEntity: editedEntity, scope = INCIDENT }) => {
  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      ...editedEntity,
      resources:
        (editedEntity as GetIncidentActionResponse | Action).resources || [],
    },
  });
  const { handleSubmit } = form;

  const { data: incidentRoster } = useIncidentRoster();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isIncident = isIncidentAction(editedEntity, scope);
  const incidentMutation = useUpdateIncidentAction();
  const organizationMutation = useEditAction();
  const mutation = isIncident ? incidentMutation : organizationMutation;

  const submitActionEdit = async (formData: FormFields) => {
    if (isIncident) {
      const payload = {
        ...formData,
        id: editedEntity.id,
        action_id: editedEntity.action_id,
      };
      await incidentMutation.mutateAsync(payload);
    } else {
      const payload = {
        ...formData,
        id: editedEntity.id,
      };
      await organizationMutation.mutateAsync(payload);
    }
    toggle();
  };

  const handleDeactivateClick = () => {
    if (!editedEntity.archived) {
      setShowConfirmModal(true);
    } else {
      toggleActive();
    }
  };

  const toggleActive = async () => {
    if (isIncident) {
      await incidentMutation.mutateAsync({
        ...editedEntity,
        id: editedEntity.id,
        archived: !editedEntity.archived,
      });
    } else {
      await organizationMutation.mutateAsync({
        ...editedEntity,
        archived: !editedEntity.archived,
      });
    }
    setShowConfirmModal(false);
    toggle();
  };

  return (
    <DrawerWrapper toggle={toggle} title="Edit Action">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(submitActionEdit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 250,
                    message: 'This maximum length is 250 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Point of Contact"
                fieldName="poc"
                options={incidentRoster?.map((u) => {
                  return {
                    label: u.userName ?? (u.emailAddress as string),
                    value: u.userId,
                  };
                })}
                variant="half"
              />
              <FormCheckboxField
                title="Briefed POC"
                fieldName="briefed_poc"
                variant="half"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Status"
                fieldName="status"
                options={ACTION_STATUS_OPTIONS}
                variant="full"
                rules={{
                  required: { value: true, message: 'Status is required' },
                }}
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Start Date & Time"
                fieldName="start_date"
                variant="full"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Deadline Date & Time"
                fieldName="target_date"
                variant="full"
              />
            </Row>
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--tertiary button--reverse"
                onClick={handleDeactivateClick}
              >
                {(!editedEntity.archived && 'Deactivate') || 'Activate'}
              </StylishNewButton>
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={mutation.isPending}
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>

      {showConfirmModal && (
        <DeleteModal
          title="Confirm Deactivation"
          onClose={() => setShowConfirmModal(false)}
          onConfirmDelete={toggleActive}
        >
          <div className="mb-3">
            Are you sure you want to deactivate this action?
          </div>
          <div className="mb-3">
            Deactivated actions will be hidden from the active actions tracker
            but preserved in the system for record-keeping.
          </div>
        </DeleteModal>
      )}
    </DrawerWrapper>
  );
};

export default OpenActionEditDrawer;
